import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Center,
  CircularProgress,
  CircularProgressLabel,
  Heading,
  Image,
  Text,
  useDisclosure,
  useInterval,
  useToast,
} from '@chakra-ui/react';
import { CapacitorWifiConnect } from '@falconeta/capacitor-wifi-connect';
import * as Sentry from '@sentry/react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useOnMount } from 'clipsal-cortex-utils/src/hooks/use-on-mount';

import semCommissioningSuccessful from '../../../../assets/images/sem_commissioning_successful.svg';
import { ArcButton } from '../../../../common/components/ArcButton';
import { SlidingAnimationPageBase } from '../../../../common/components/SlidingAnimationPageBase';
import { TopNavProgressLoader } from '../../../../common/components/TopNavProgressLoader';
import { WifiNetwork, WifiNetworkList } from '../../../../common/components/WifiNetworkList';
import { timeout } from '../../../../utils/common/common-utils';
import { MeterWifiPasswordDialog } from './MeterWifiPasswordDialog';
import { makeRequestWithAuth } from './utils';
import { ConnectionTestOngoing, ConnectionTestResults, DiscoveredNetwork } from './wireless-access-api-types';

type State = {
  isLoaded: boolean;
  wifiNetworks: WifiNetwork[];
  selectedWifiNetworkIndex: number | null;
  selectedWifiNetworkPassword: string;
  connectionTestProgress: number; // Value between 1 and 100
  connectionTestResults: 'NOT_STARTED' | 'IN_PROGRESS' | 'SUCCESS' | 'READY_TO_REGISTER' | 'TEST_FAILED';
};

const INITIAL_STATE: State = {
  isLoaded: false,
  wifiNetworks: [],
  selectedWifiNetworkIndex: null,
  selectedWifiNetworkPassword: '',
  connectionTestProgress: 0,
  connectionTestResults: 'NOT_STARTED',
};

/* istanbul ignore next -- @preserve */
export function MeterConnectionTests() {
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const [state, setState] = useState(INITIAL_STATE);
  const {
    isLoaded,
    wifiNetworks,
    selectedWifiNetworkIndex,
    selectedWifiNetworkPassword,
    connectionTestProgress,
    connectionTestResults,
  } = state;
  const { onOpen, onClose, isOpen } = useDisclosure();
  const accountId = search.get('accountId');
  const isRegisteredDevice = search.get('shouldRegisterDevice') === 'false';
  const toast = useToast({ isClosable: true });
  const { t } = useTranslation();
  const connectionTestResultsRef = useRef(connectionTestResults);

  const urlSearchParamsToGoBack = useMemo(() => {
    search.set('direction', 'back');
    return search.toString();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchLocalWifiNetworks = useCallback(async () => {
    try {
      setState((p) => ({
        ...p,
        isLoaded: false,
        selectedWifiNetworkIndex: null,
        wifiNetworks: [],
      }));
      const networks = await makeRequestWithAuth(accountId, 'GET', '/api/wifi_scan');
      const discoveredNetworks = networks as unknown as DiscoveredNetwork[];
      setState((p) => ({
        ...p,
        isLoaded: true,
        wifiNetworks: discoveredNetworks.map<WifiNetwork>(({ n, s, e }) => ({
          signal: s,
          ssid: n,
          security: e,
        })),
        selectedWifiNetworkIndex: null,
      }));
    } catch (e) {
      const error = e as Error & {
        status?: number;
        response?: { data?: unknown; status?: number; statusText?: string };
        message?: string;
      };

      Sentry.captureException(e, {
        tags: {
          category: 'feature',
          feature: 'meter',
          component: 'MeterConnectionTests',
          method: 'fetchLocalWifiNetworks',
        },
        extra: {
          errorStatus: error.status || error.response?.status,
          errorStatusText: error.response?.statusText,
          errorMessage: error.message,
          errorName: error.name,
          responseData: JSON.stringify(error.response?.data),
        },
      });
      console.error('Full error object:', JSON.stringify(e, null, 2));
      toast.closeAll();

      /* istanbul ignore if -- @preserve */
      if (error?.name === 'AccountLockedSEM') {
        toast({
          title: t('Set Up Hardware.sem incorrectly configured'),
          description: `${t('Common.please contact support')} ${t('Set Up Hardware.sem likely connected wrong fleet')}`,
          status: 'error',
        });
      } else {
        toast({
          title: t('Set Up Hardware.error fetching wifi'),
          description: `${t('Common.please try again')} ${t('Common.if this persists contact support')}`,
          status: 'error',
        });
      }
      setState((p) => ({
        ...p,
        isLoaded: true,
      }));
    }
  }, [accountId, t, toast]);

  useOnMount(fetchLocalWifiNetworks);

  /* istanbul ignore next -- @preserve */
  const runConnectionTests = useCallback(async () => {
    try {
      search.set('if', 'wifi');

      // Next, we begin the device's connection tests (this actual trigger endpoint does not return any data, instead
      // it begins an asynchronous process on the device that will eventually return the connection test results).
      // We poll for this result with the `get` call below.
      await makeRequestWithAuth(
        accountId,
        'POST',
        '/api/start_conn_test',
        { if: 'wifi' },
        {
          'Content-Type': 'application/x-www-form-urlencoded',
        }
      );

      setState((p) => ({ ...p, connectionTestResults: 'IN_PROGRESS' }));
    } catch (e) {
      const error = e as Error;
      Sentry.captureException(e, {
        tags: {
          category: 'feature',
          feature: 'meter',
          component: 'MeterConnectionTests',
          method: 'runConnectionTests',
        },
      });
      console.error(e);
      if (error?.name === 'AccountLockedSEM') {
        toast({
          title: t('Set Up Hardware.sem incorrectly configured'),
          description: `${t('Common.please contact support')} ${t('Set Up Hardware.sem likely connected wrong fleet')}`,
          status: 'error',
        });
      } else {
        toast({
          title: t('Set Up Hardware.error running commissioning tests'),
          description: `${t('Common.please try again')} ${t('Common.if this persists contact support')}`,
          status: 'error',
        });
      }
    }
  }, [accountId, search, t, toast]);

  useEffect(() => {
    connectionTestResultsRef.current = connectionTestResults;
  }, [connectionTestResults]);

  /* istanbul ignore next -- @preserve */
  useInterval(
    async () => {
      if (connectionTestResultsRef.current !== 'IN_PROGRESS') return;

      // Poll for the connection test result, and once complete, examine the result to ensure the device is
      // connected to the internet through the gateway.
      // After this is complete and successful, we can finally register the device in the Clipsal and Sense clouds.
      try {
        const ctr = await makeRequestWithAuth(accountId, 'GET', '/api/conn_test_result');
        const connectionTestResult = ctr as unknown as ConnectionTestOngoing | ConnectionTestResults;

        // @TODO: uncomment when self-commissioning API bug has been resolved by Sense
        // const status = await get<MonitorStatus>(`${BASE_IPV4_ADDRESS}/api/status`);
        // // If the status is failing, inform the user and do not allow them to proceed.
        // if ('w' in status && !!status.w && !status.w.R) {
        //   setConnectionStatus('STATUS_CHECK_FAILED');

        if ('r' in connectionTestResult) {
          const didTestsPass = connectionTestResult.r.every(([_, status]) => status !== 'f');
          if (didTestsPass) {
            return setState({
              ...state,
              connectionTestResults: 'SUCCESS',
            });
          } else {
            toast({
              title: t('Set Up Hardware.error running commissioning tests'),
              description: t('Set Up Hardware.please ensure the network has internet'),
              status: 'error',
            });
            return setState({
              ...state,
              connectionTestResults: 'TEST_FAILED',
            });
          }
        } else {
          const { c, tt } = connectionTestResult;
          setState({
            ...state,
            connectionTestProgress: Math.round((c / tt) * 100),
          });
        }
      } catch (e) {
        const error = e as Error;
        Sentry.captureException(e, {
          tags: {
            category: 'feature',
            feature: 'meter',
            component: 'MeterConnectionTests',
            method: 'useInterval',
          },
        });
        console.error(e);
        if (error?.name === 'AccountLockedSEM') {
          toast({
            title: t('Set Up Hardware.sem incorrectly configured'),
            description: `${t('Common.please contact support')} 
            ${t('Set Up Hardware.sem likely connected wrong fleet')}`,
            status: 'error',
          });
        } else {
          toast({
            title: t('Set Up Hardware.error running commissioning tests'),
            description: `${t('Common.please try again')} ${t('Common.if this persists contact support')}`,
            status: 'error',
          });
        }
        return setState({
          ...state,
          connectionTestResults: 'TEST_FAILED',
          connectionTestProgress: 0,
        });
      }
    },
    connectionTestResults === 'IN_PROGRESS' ? 2_000 : null
  );

  /* istanbul ignore next -- @preserve */
  useEffect(() => {
    async function reconnectToHomeWifi() {
      try {
        // Here we reconnect the user back from the SEM WAP back to their home Wi-Fi
        await CapacitorWifiConnect.secureConnect({
          ssid: wifiNetworks[selectedWifiNetworkIndex!].ssid,
          password: selectedWifiNetworkPassword,
        });

        // Here we wait a bit after connecting to the Wi-Fi before moving on to ensure we are connected -
        // - before immediately making any network requests
        await timeout(8_000);
      } catch (e) {
        Sentry.captureException(e, {
          tags: {
            category: 'feature',
            feature: 'meter',
            component: 'MeterConnectionTests',
            method: 'useEffect',
          },
        });
        console.error(e);
        toast({
          title: t('Set Up Hardware.error connecting to wifi'),
          description: `${t('Common.please try again')} ${t('Common.if this persists contact support')}`,
          status: 'error',
        });
      }
      setState((p) => ({ ...p, connectionTestResults: 'READY_TO_REGISTER' }));
    }

    if (connectionTestResults === 'SUCCESS') reconnectToHomeWifi();

    if (connectionTestResults === 'READY_TO_REGISTER') {
      if (isRegisteredDevice) {
        navigate(`../finish?${search.toString()}`);
      } else {
        navigate(`../register?${search.toString()}`);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectionTestResults]);

  function handleOnClickWifiNetwork(index: number | null) {
    // Clicking the same network again unselects it
    const newIndex = index === selectedWifiNetworkIndex ? null : index;
    setState((prev) => ({ ...prev, selectedWifiNetworkIndex: newIndex }));
  }

  let content;

  /* istanbul ignore if -- @preserve */
  if (connectionTestResults === 'IN_PROGRESS') {
    content = (
      <>
        <TopNavProgressLoader />
        <Heading mb={6} size="md">
          {t('Set Up Hardware.checking network connection')}...
        </Heading>
        <CircularProgress value={connectionTestProgress} thickness="6px" size="200px" color={'primaryBranding.500'}>
          <CircularProgressLabel>{connectionTestProgress}%</CircularProgressLabel>
        </CircularProgress>
      </>
    );
  }

  /* istanbul ignore if -- @preserve */
  if (connectionTestResults === 'SUCCESS' || connectionTestResults === 'READY_TO_REGISTER') {
    content = (
      <>
        <TopNavProgressLoader />
        <Image mb={3} w={'70%'} src={semCommissioningSuccessful} alt="SEM configuration complete" />
        <Heading mt={3} size="md">
          {t('Set Up Hardware.sem connected to internet')}
        </Heading>
        <Text mt={2}>{t('Set Up Hardware.give us a minute')}</Text>
      </>
    );
  }

  // We assume the user is connected to the SEM WAP in this case.
  if (connectionTestResults === 'NOT_STARTED' || connectionTestResults === 'TEST_FAILED') {
    content = (
      <>
        <Heading size="lg">{t('Set Up Hardware.select your wifi')}</Heading>
        <Text my={1}>{t('Set Up Hardware.choose the wifi for sem')}</Text>

        <WifiNetworkList
          wifiNetworks={wifiNetworks}
          selectedIndex={selectedWifiNetworkIndex}
          onClick={handleOnClickWifiNetwork}
          isLoading={!isLoaded}
        />

        <ArcButton
          hidden={!(selectedWifiNetworkIndex === null)}
          data-testid="retry-button"
          onClick={fetchLocalWifiNetworks}
          isLoading={!isLoaded}
          mt={8}
          w={'80%'}
          arcColor="#3DCD57"
        >
          {t('Set Up Hardware.scan networks')}
        </ArcButton>

        <ArcButton
          hidden={selectedWifiNetworkIndex === null}
          onClick={onOpen}
          mt={8}
          w={'80%'}
          arcColor="#3DCD57"
          isDisabled={selectedWifiNetworkIndex === null}
        >
          {t('Common.continue')}
        </ArcButton>
      </>
    );
  }

  return (
    <SlidingAnimationPageBase
      title={t('Common.device setup')}
      backURL={`../connect_instructions?${urlSearchParamsToGoBack}`}
    >
      <Center flexDirection="column" px={4} mt={6} textAlign="center">
        {content}
      </Center>

      <MeterWifiPasswordDialog
        network={wifiNetworks[selectedWifiNetworkIndex!]}
        isOpen={isOpen}
        onClose={onClose}
        onConfirm={({ password }) => {
          setState((p) => ({
            ...p,
            selectedWifiNetworkPassword: password,
          }));
          runConnectionTests();
        }}
      />
    </SlidingAnimationPageBase>
  );
}
